import { Toaster } from "../../ui/toaster";
import { Toaster as Sonner } from "../../ui/sonner";
import { TooltipProvider } from "../../ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AppLayout } from "../../components/PortfolioPerformance/AppLayout";
import NetInvestment from "./pages/NetInvestment";
import IndustryBenchmark from "./pages/IndustryBenchmark";
import ExposureTracking from "./pages/ExposureTracking";
import Watchlist from "./pages/Watchlist";
import ApdMonitoring from "./pages/ApdMonitoring";
import Epd from "./pages/Epd";
import MacroExposure from "./pages/MacroExposure";
import LossRecovery from "./pages/LossRecovery";
import Lgd from "./pages/Lgd";
import ExpectedLoss from "./pages/ExpectedLoss";
import LossCalibration from "./pages/LossCalibration";
import AnnualizedLoss from "./pages/AnnualizedLoss";
import LossDevelopmentWal from "./pages/LossDevelopmentWal";
import ChargeoffAcceleration from "./pages/ChargeoffAcceleration";
import EarningsReturn from "./pages/EarningsReturn";
import LifetimePrepaymentProjections from "./pages/LifetimePrepaymentProjections";
import LifetimeRepeatCustomerProjections from "./pages/LifetimeRepeatCustomerProjections";
import Snapshot from "./pages/Snapshot";
import Attestation from "./pages/Attestation";
import ManageAccess from "./pages/ManageAccess";
import "../../assets/css/PortfolioPerformance/style.css";
import { Routes, Route, Navigate, Link } from "react-router-dom";
import React, { useEffect, useState } from "react";
import axios from "axios";
import UserService from "../../services/UserService";
import serverConfig from "../../config/server";
import { mapUrlToProductId } from "../../utils/common/mapUrlToProduct";
import { isProductAdmin } from "../../components/PortfolioPerformance/access-control";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => {
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  // "Denied" and "could not be determined" are different answers and used to render
  // identically, as "You don't have an active subscription" -- so a dropped MySQL tunnel
  // or an expired session read as an entitlement problem and got debugged as one.
  const [accessError, setAccessError] = useState<string | null>(null);

  useEffect(() => {
    const productId = mapUrlToProductId("portfolio-performance");
    UserService.ensureFreshToken()
      .then((jwt: string) =>
        axios.post(`${serverConfig.api_base_url}check-product-access`, {
          jwt,
          product_id: productId,
        })
      )
      .then((res: any) => {
        // The gateway returns 200 with a status field, so a 401/500 body arrives here
        // rather than in .catch -- has_access is simply absent on those.
        if (res.data?.has_access === true) {
          setHasAccess(true);
          return;
        }
        setHasAccess(false);
        if (res.data?.status && res.data.status !== 200) {
          setAccessError(res.data.message || `Access check failed (${res.data.status}).`);
        }
      })
      .catch((e: any) => {
        setHasAccess(false);
        setAccessError(
          e?.response?.data?.message ||
            e?.message ||
            "The access check could not reach the platform API."
        );
      })
      .finally(() => {
        setAccessChecked(true);
      });
  }, []);

  // Important: shadcn/Radix components render via portals into <body>.
  // If we scope theme tokens only to an inner wrapper, portal content won't inherit them.
  // So while PortfolioPerformance is mounted, apply a root class + dark mode on <html>.
  React.useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    root.classList.add("portfolio-performance-root", "dark");
    body.style.backgroundColor = "hsl(218, 30%, 8%)";
    body.style.color = "hsl(30, 9%, 95%)";
    body.style.margin = "0";
    body.style.padding = "0";
    return () => {
      root.classList.remove("portfolio-performance-root", "dark");
      body.style.backgroundColor = "";
      body.style.color = "";
      body.style.margin = "";
      body.style.padding = "";
    };
  }, []);

  // The gate selects what the product's own routes render -- it deliberately does NOT
  // wrap <Routes>, because /manage-access has to stay reachable when the gate says no.
  // An account holding only "72-admin" is denied here by design (platform-api's
  // has_any_product_role counts only user/contributor/reviewer as working tiers), and
  // Manage Access is the one place it can grant itself or anyone else a working tier.
  // Same structure, and the same reason, as AthenaPricingEngine.tsx/UnderwritingEngineV2.tsx.
  let gatedElement: React.ReactNode;
  if (!accessChecked) {
    gatedElement = (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <p className="text-muted-foreground text-sm">Checking access…</p>
      </div>
    );
  } else if (!hasAccess) {
    gatedElement = (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="max-w-md text-center space-y-2 px-6">
          <h2 className="text-lg font-semibold" style={{ color: "#FD9F29" }}>
            {accessError ? "Access Check Failed" : "Access Denied"}
          </h2>
          <p className="text-sm text-muted-foreground">
            {accessError
              ? accessError
              : "You don't have an active subscription to this product."}
          </p>
          {accessError && (
            <p className="text-xs text-muted-foreground/70">
              This is not an entitlement decision — the check could not complete. Usual
              causes: the MySQL tunnel behind platform-api is down, or your session
              expired.
            </p>
          )}
          {!accessError && isProductAdmin() && (
            <Link
              to="/portfolio-performance/manage-access"
              className="inline-block text-sm text-amber hover:underline"
            >
              Manage Access →
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <div
          className="portfolio-performance-theme dark"
          style={{
            backgroundColor: 'hsl(218, 30%, 8%)',
            minHeight: '100vh',
            color: 'hsl(30, 9%, 95%)'
          }}
        >
          <Routes>
            {/* Outside the product gate, and outside AppLayout -- it is a standalone
                full-screen page that gates itself on the admin role. */}
            <Route path="manage-access" element={<ManageAccess />} />

            {gatedElement ? (
              <Route path="*" element={gatedElement} />
            ) : (
              <Route element={<AppLayout />}>
                <Route index element={<Navigate to="net-investment" replace />} />
                <Route path="net-investment" element={<NetInvestment />} />
                <Route path="industry-benchmark" element={<IndustryBenchmark />} />
                <Route path="exposure-tracking" element={<ExposureTracking />} />
                <Route path="watchlist" element={<Watchlist />} />
                <Route path="apd-monitoring" element={<ApdMonitoring />} />
                <Route path="epd" element={<Epd />} />
                <Route path="macro-exposure" element={<MacroExposure />} />
                <Route path="loss-recovery" element={<LossRecovery />} />
                <Route path="lgd" element={<Lgd />} />
                <Route path="expected-loss" element={<ExpectedLoss />} />
                <Route path="loss-calibration" element={<LossCalibration />} />
                <Route path="annualized-loss" element={<AnnualizedLoss />} />
                <Route path="loss-development-wal" element={<LossDevelopmentWal />} />
                <Route
                  path="chargeoff-acceleration"
                  element={<ChargeoffAcceleration />}
                />
                <Route path="earnings-return" element={<EarningsReturn />} />
                <Route
                  path="lifetime-prepayment-projections"
                  element={<LifetimePrepaymentProjections />}
                />
                <Route
                  path="lifetime-repeat-customer-projections"
                  element={<LifetimeRepeatCustomerProjections />}
                />
                <Route path="snapshot" element={<Snapshot />} />
                <Route path="attestation" element={<Attestation />} />
                <Route
                  path="*"
                  element={<Navigate to="/portfolio-performance/net-investment" replace />}
                />
              </Route>
            )}
          </Routes>
        </div>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
